<template>
    <div
        class="min-h-16 pt-4 pb-4 mb-2 z-20 flex flex-col flex-wrap sm:flex-row justify-between items-start sm:items-center border-b"
        :class="{
            'sticky top-0 bg-gray-100': !isSlideOver,
        }"
    >
        <div class="w-1/2">
            <a
                v-if="indexRoute && indexValue"
                class="flex mb-2 text-blue-600 text-xs"
                :href="indexRoute"
            >
                ← {{ indexValue }}
            </a>

            <div class="flex">

                <div v-if="seoData" class="seoDataIcon mr-1">
                    <svg v-if="seoIsGreen" @click="showSeoData = !showSeoData" xmlns="http://www.w3.org/2000/svg" class="seoDataIcon text-green-700 cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>

                    <svg v-if="seoIsAmber" @click="showSeoData = !showSeoData" xmlns="http://www.w3.org/2000/svg" class="seoDataIcon text-yellow-500 cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>

                <h3 class="text-xl font-medium mb-2 sm:mb-0">
                    {{ title }}
                </h3>

                <p v-if="subtitle" class="text-gray-500 mt-2">
                    {{ subtitle }}
                </p>
            </div>

            <div v-if="initVersionId">
                <small class="font-medium text-blue-500">Version {{ initVersionId }}</small>
            </div>

            <div v-if="debugStore.debugMode" class="mt-4">
                <small class="font-medium text-red-500">Page has unsaved changes: {{ dirtyStore.isDirty ? 'Yes' : 'No' }}</small>
            </div>

        </div>

        <div
            class="w-1/2 flex flex-col sm:flex-row justify-end"
        >
            <Btn
                :is-main="true"
                :type="submitButtonType"
                @click="(e) => $emit('click', e)"
                :content="submitButtonText"
                v-if="showSubmitButton"
                :disabled="submitButtonDisabled || !hasLock"
            />

            <button
                v-if="hasDropDownOptions"
                class="border-0 bg-0 text-blue-600 flex flex-row items-center px-3"
                type="button"
                @click="dropDownOpen = !dropDownOpen"
            >
                <span class="text-gray-400">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="3"
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </span>
            </button>
            <transition
                v-if="hasDropDownOptions"
                name="dropdown"
            >
                <div
                    v-show="dropDownOpen"
                    v-on-click-outside="() => { if (dropDownOpen) dropDownOpen = false }"
                    class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg z-10 top-14"
                >
                    <div
                        class="rounded-md bg-white ring-1 ring-black ring-opacity-5"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="options-menu"
                    >
                        <div class="py-1">
                            <a
                                v-if="showCreateButton"
                                :href="createDisabled ? null : createRoute"
                                target="_blank"
                                class="cursor-pointer block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                :class="[
                                    createDisabled ? 'opacity-50 cursor-not-allowed' : null,
                                ]"
                                v-tooltip="{ content: createTooltip || '', container: false, placement: 'bottom', html: false, }"
                                role="menuitem"
                            >
                                {{ createValue }}
                            </a>

                            <a
                                class="cursor-pointer block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                target="_blank"
                                v-if="showPreviewButton"
                                @click.prevent="(e) => $emit('click-preview', e)"
                                role="menuitem"
                            >
                                Preview
                            </a>

                            <a
                                class="cursor-pointer block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                target="_blank"
                                v-if="notionPage"
                                :href="notionPage"
                                role="menuitem"
                            >
                                Notion Page
                            </a>

                            <delete-button
                                v-if="showDeleteButton && hasLock"
                                :delete-route="deleteRoute"
                                :value="deleteValue"
                                :delete-options="deleteOptions"
                                :delete-disabled="deleteDisabled"
                                :delete-tooltip="deleteTooltip"
                                :use-anchor-tag="true"
                                btn-class="cursor-pointer block px-4 py-2 text-sm leading-5 focus:outline-none bg-red-600 hover:bg-red-500 focus:border-red-700 focus:ring-red text-white"
                            />
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <div v-if="initVersionId && versionId !== initVersionId" class="w-full bg-red-100 border border-red-400 text-red-700 px-4 py-3 mt-4 rounded relative" role="alert">
            <strong class="font-bold">Document has been updated <span v-if="lastUpdatedBy">by {{ lastUpdatedBy }}</span></strong>
            <span class="block mt-2">Please reload page</span>
            <span class="block mt-2">Latest version: {{ versionId }}</span>
        </div>

        <lock-status
            v-if="lockable"
            :has-lock="hasLock"
            :document-lock="documentLock"
            @force-takeover="$emit('force-takeover')"
            @takeover="$emit('takeover')"
        />

        <errors :errors="errorMessages" />

        <div class="mt-2 flex items-end" v-if="showSeoData">

            <page-seo :data="seoData"></page-seo>
            <p v-if="showSeoData" class="text-xs underline ml-2 cursor-pointer" @click="showSeoData = false">Hide</p>
        </div>
    </div>
</template>

<script>
import {useDebugStore} from "../../stores/debug";
import {useDirtyStore} from "../../stores/dirty";

export default {
    name: "PageHeader",
    computed: {
        showCreateButton () {
            return this.createValue && this.createRoute && this.canCreate;
        },
        showDeleteButton () {
            return this.deleteRoute && this.deleteValue && this.canDelete;
        },
        hasDropDownOptions () {
            return this.showDeleteButton || this.showCreateButton || this.showPreviewButton;
        },
        seoIsGreen: function(){
            if(this.seoData === null || this.seoData === undefined) return false;
            return this.seoData.lighthouse_accessibility_score >= 90 &&
                this.seoData.lighthouse_best_practices_score >= 90 &&
                this.seoData.lighthouse_performance_score >= 90 &&
                this.seoData.lighthouse_seo_score >= 90;
        },
        seoIsAmber: function(){
            if(this.seoData === null || this.seoData === undefined) return false;
            return this.seoData.lighthouse_accessibility_score <= 89 ||
                this.seoData.lighthouse_best_practices_score <= 89 ||
                this.seoData.lighthouse_performance_score <= 89 ||
                this.seoData.lighthouse_seo_score <= 89;
        },
        errorMessages: function() {
            let mapped = {};

            // don't display table error messages, because it's a json string
            // replace with generic simple message, the details will be handled by the table element
            for (let [key, value] of Object.entries(this.errors)) {
                if (/^contentBlocks\.\d+\.sections\.\d+\.table$/.test(key)
                    || key === 'table') {
                    mapped[key] = ['The table element has errors'];
                } else {
                    mapped[key] = value;
                }
            }

            return mapped;
        },
    },
    data() {
        return {
            dropDownOpen: false,
            showSeoData: false,
            notionPage: "",
            debugStore: useDebugStore(),
            dirtyStore: useDirtyStore(),
        };
    },
    props: {
        versionId: {
            type: Number,
            required: false,
            default: null,
        },
        initVersionId: {
            type: Number,
            required: false,
            default: null,
        },
        lastUpdatedBy: {
            type: String,
            required: false,
            default: null,
        },
        lockable: {
            type: Boolean,
            required: false,
            default: false,
        },
        hasLock: {
            type: Boolean,
            required: false,
            default: true,
        },
        documentLock: {
            type: Object,
            required: false,
        },
        submitButtonText: {
            type: String,
            required: false,
            default: "Update",
        },
        showSubmitButton: {
            type: Boolean,
            required: false,
            default: true,
        },
        submitButtonDisabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        submitButtonType:{
            type: String,
            default: "submit"
        },

        canDelete: {
            type: Boolean,
            required: false,
            default: true,
        },
        deleteRoute: {
            type: String,
            required: false,
            default: "",
        },
        deleteValue: {
            type: String,
            required: false,
            default: "",
        },
        deleteDisabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        deleteTooltip: {
            type: String,
            required: false
        },
        deleteOptions: {
            type: Object,
            required: false,
        },

        canCreate: {
            type: Boolean,
            required: false,
            default: true,
        },
        createRoute: {
            type: String,
            required: false,
            default: "",
        },
        createValue: {
            type: String,
            required: false,
            default: "",
        },
        createDisabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        createTooltip: {
            type: String,
            required: false
        },

        showPreviewButton: {
            type: Boolean,
            required: false,
            default: false,
        },

        indexRoute: {
            type: String,
            required: false,
            default: "",
        },
        indexValue: {
            type: String,
            required: false,
            default: "",
        },
        title: {
            type: String,
            required: false,
            default: "",
        },
        subtitle: {
            type: String,
            required: false,
            default: "",
        },

        errors: {
            type: [Object, Array],
            required: true,
        },
        isSlideOver: {
            type: Boolean,
            required: false,
            default: false,
        },
        seoData: {
            type: Object,
            required: false,
        },
        model: {
            type: String,
            required: false,
        }
    },
    mounted: function(){
        axios.post(route('api.pageDocuments.find', {model: this.model}))
        .then(resp => {
            this.notionPage = resp?.data?.notion_page
        })
        .catch(error => {
            console.log('error', error)
        })
    }
};
</script>
